import CurrencyFormatter from "currency-formatter";

export default class FinancialAccount {
  /**
   * @param {string} accountId
   * @param {string} parentAccountId
   * @param {string} name
   * @param {float} value
   * @param {array<FinancialAccount>} items
   */
  constructor({
    accountId,
    parentAccountId,
    name,
    value,
    items = [],
    category,
    date,
  }) {
    this.accountId = accountId;
    this.parentAccountId = parentAccountId;
    this.name = name;
    this.value = value;
    this.items = items;
    this.category = category;
    this.date = date;
  }

  get formattedValue() {
    return CurrencyFormatter.format(this.value, { code: "USD", symbol: "$ " });
  }

  static getAllowedAssetsCategories(options = { group: null }) {
    const { currentAssets, longTermAssets } =
      this.getAllowedBalanceSheetItemGroups();

    let allowedCategories = {
      CASH_CATEGORY: {
        name: "Cash",
        value: "CASH_CATEGORY",
        group: currentAssets,
      },
      MARKETABLE_SECURITIES_CATEGORY: {
        name: "Marketable Securities",
        value: "MARKETABLE_SECURITIES_CATEGORY",
        group: currentAssets,
      },
      ACCOUNTS_RECEIVABLE_CATEGORY: {
        name: "Accounts Receivable",
        value: "ACCOUNTS_RECEIVABLE_CATEGORY",
        group: currentAssets,
      },
      INVENTORY_CATEGORY: {
        name: "Inventory",
        value: "INVENTORY_CATEGORY",
        group: currentAssets,
      },
      OTHER_CURRENT_ASSET_CATEGORY: {
        name: "Other Current Asset",
        value: "OTHER_CURRENT_ASSET_CATEGORY",
        group: currentAssets,
      },
      GROSS_FIXED_ASSETS_CATEGORY: {
        name: "Gross Fixed Assets",
        value: "GROSS_FIXED_ASSETS_CATEGORY",
        group: longTermAssets, //gross fixed assets belong to long term assets(fixed assets)
      },
      REAL_ESTATE_ASSET_CATEGORY: {
        name: "Real Estate Asset",
        value: "REAL_ESTATE_ASSET_CATEGORY",
        group: longTermAssets,
      },
      OTHER_LONG_TERM_ASSET_CATEGORY: {
        name: "Other Long Term Asset",
        value: "OTHER_LONG_TERM_ASSET_CATEGORY",
        group: longTermAssets,
      },
    };

    if (options.group) {
      const categoriesToBeDeleted = Object.keys(allowedCategories).filter(
        (category) => allowedCategories[category].group !== options.group
      );

      categoriesToBeDeleted.forEach((categoryToBeDeleted) => {
        delete allowedCategories[categoryToBeDeleted];
      });

      return allowedCategories;
    }

    return allowedCategories;
  }

  static getAssetsCategoryOptions() {
    const {
      CASH_CATEGORY,
      MARKETABLE_SECURITIES_CATEGORY,
      ACCOUNTS_RECEIVABLE_CATEGORY,
      INVENTORY_CATEGORY,
      OTHER_CURRENT_ASSET_CATEGORY,
      REAL_ESTATE_ASSET_CATEGORY,
      OTHER_LONG_TERM_ASSET_CATEGORY,
      GROSS_FIXED_ASSETS_CATEGORY,
    } = this.getAllowedAssetsCategories();

    return [
      {
        name: CASH_CATEGORY.name,
        value: CASH_CATEGORY.value,
        group: CASH_CATEGORY.group,
      },
      {
        name: MARKETABLE_SECURITIES_CATEGORY.name,
        value: MARKETABLE_SECURITIES_CATEGORY.value,
        group: MARKETABLE_SECURITIES_CATEGORY.group,
      },
      {
        name: ACCOUNTS_RECEIVABLE_CATEGORY.name,
        value: ACCOUNTS_RECEIVABLE_CATEGORY.value,
        group: ACCOUNTS_RECEIVABLE_CATEGORY.group,
      },
      {
        name: INVENTORY_CATEGORY.name,
        value: INVENTORY_CATEGORY.value,
        group: INVENTORY_CATEGORY.group,
      },
      {
        name: OTHER_CURRENT_ASSET_CATEGORY.name,
        value: OTHER_CURRENT_ASSET_CATEGORY.value,
        group: OTHER_CURRENT_ASSET_CATEGORY.group,
      },
      {
        name: GROSS_FIXED_ASSETS_CATEGORY.name,
        value: GROSS_FIXED_ASSETS_CATEGORY.value,
        group: GROSS_FIXED_ASSETS_CATEGORY.group,
      },
      {
        name: REAL_ESTATE_ASSET_CATEGORY.name,
        value: REAL_ESTATE_ASSET_CATEGORY.value,
        group: REAL_ESTATE_ASSET_CATEGORY.group,
      },
      {
        name: OTHER_LONG_TERM_ASSET_CATEGORY.name,
        value: OTHER_LONG_TERM_ASSET_CATEGORY.value,
        group: OTHER_LONG_TERM_ASSET_CATEGORY.group,
      },
    ];
  }

  static getAllowedBalanceSheetItemGroups() {
    return {
      currentAssets: "Current Assets",
      longTermAssets: "Fixed Assets", //long term assets are described as fixed assets in balance sheet
      currentLiabilities: "Current Liabilities",
      longTermLiabilities: "Long Term Liability", //long term liabilities are described as long term liability in balance sheet
    };
  }

  static getAllowedLiabilitiesCategories(options = { group: null }) {
    const { currentLiabilities, longTermLiabilities } =
      this.getAllowedBalanceSheetItemGroups();
    let allowedCategories = {
      ACCOUNTS_PAYABLE_CATEGORY: {
        name: "Accounts Payable",
        value: "ACCOUNTS_PAYABLE_CATEGORY",
        group: currentLiabilities,
      },
      OTHER_CURRENT_LIABILITIES_CATEGORY: {
        name: "Other Current Liabilities",
        value: "OTHER_CURRENT_LIABILITIES_CATEGORY",
        group: currentLiabilities,
      },
      OTHER_LONG_TERM_LIABILITY_CATEGORY: {
        name: "Other Long Term Liability",
        value: "OTHER_LONG_TERM_LIABILITY_CATEGORY",
        group: longTermLiabilities,
      },
      LONG_TERM_LIABILITIES_CATEGORY: {
        name: "Long Term Liabilities",
        value: "LONG_TERM_LIABILITIES_CATEGORY",
        group: longTermLiabilities,
      },
      LOANS_AND_NOTES_PAYABLE: {
        name: "Loans/Notes Payable",
        value: "LOANS_AND_NOTES_PAYABLE",
        group: currentLiabilities,
      },
    };

    if (options.group) {
      const categoriesToBeDeleted = Object.keys(allowedCategories).filter(
        (category) => allowedCategories[category].group !== options.group
      );

      categoriesToBeDeleted.forEach((categoryToBeDeleted) => {
        delete allowedCategories[categoryToBeDeleted];
      });

      return allowedCategories;
    }

    return allowedCategories;
  }

  static getLiabilitiesCategoryOptions() {
    const {
      ACCOUNTS_PAYABLE_CATEGORY,
      LOANS_AND_NOTES_PAYABLE,
      OTHER_CURRENT_LIABILITIES_CATEGORY,
      LONG_TERM_LIABILITIES_CATEGORY,
      OTHER_LONG_TERM_LIABILITY_CATEGORY,
    } = this.getAllowedLiabilitiesCategories();

    return [
      {
        name: ACCOUNTS_PAYABLE_CATEGORY.name,
        value: ACCOUNTS_PAYABLE_CATEGORY.value,
        group: ACCOUNTS_PAYABLE_CATEGORY.group,
      },
      {
        name: LOANS_AND_NOTES_PAYABLE.name,
        value: LOANS_AND_NOTES_PAYABLE.value,
        group: LOANS_AND_NOTES_PAYABLE.group,
      },
      {
        name: OTHER_CURRENT_LIABILITIES_CATEGORY.name,
        value: OTHER_CURRENT_LIABILITIES_CATEGORY.value,
        group: OTHER_CURRENT_LIABILITIES_CATEGORY.group,
      },
      {
        name: LONG_TERM_LIABILITIES_CATEGORY.name,
        value: LONG_TERM_LIABILITIES_CATEGORY.value,
        group: LONG_TERM_LIABILITIES_CATEGORY.group,
      },
      {
        name: OTHER_LONG_TERM_LIABILITY_CATEGORY.name,
        value: OTHER_LONG_TERM_LIABILITY_CATEGORY.value,
        group: OTHER_LONG_TERM_LIABILITY_CATEGORY.group,
      },
    ];
  }

  static getAllowedEquitiesCategories() {
    return {
      COMMON_STOCK_CATEGORY: {
        name: "Common Stock",
        value: "COMMON_STOCK_CATEGORY",
      },
      PREFERRED_STOCK_CATEGORY: {
        name: "Preferred Stock",
        value: "PREFERRED_STOCK_CATEGORY",
      },
      TREASURY_STOCK_CATEGORY: {
        name: "Treasury Stock",
        value: "TREASURY_STOCK_CATEGORY",
      },
      RETAINED_EARNINGS_CATEGORY: {
        name: "Retained Earnings",
        value: "RETAINED_EARNINGS_CATEGORY",
      },
      ADDITIONAL_PAID_IN_CAPITAL_CATEGORY: {
        name: "Additional Paid-In Capital",
        value: "ADDITIONAL_PAID_IN_CAPITAL_CATEGORY",
      },
      DIVIDENDS_CATEGORY: {
        name: "Dividends",
        value: "DIVIDENDS_CATEGORY",
      },
      OWNER_DISTRIBUTION_CATEGORY: {
        name: "Owner's Distribution",
        value: "OWNER_DISTRIBUTION_CATEGORY",
      },
      OWNER_AND_MEMBER_AND_PARTNER_CAPITAL_CATEGORY: {
        name: "Owner, Member, Partner Capital",
        value: "OWNER_AND_MEMBER_AND_PARTNER_CAPITAL_CATEGORY",
      },
      NET_INCOME_CATEGORY: {
        name: "Net Income",
        value: "NET_INCOME_CATEGORY",
      },
    };
  }

  static getEquitiesCategoryOptions() {
    const {
      COMMON_STOCK_CATEGORY,
      PREFERRED_STOCK_CATEGORY,
      TREASURY_STOCK_CATEGORY,
      RETAINED_EARNINGS_CATEGORY,
      ADDITIONAL_PAID_IN_CAPITAL_CATEGORY,
      DIVIDENDS_CATEGORY,
      OWNER_DISTRIBUTION_CATEGORY,
      OWNER_AND_MEMBER_AND_PARTNER_CAPITAL_CATEGORY,
      NET_INCOME_CATEGORY,
    } = this.getAllowedEquitiesCategories();

    return [
      {
        name: COMMON_STOCK_CATEGORY.name,
        value: COMMON_STOCK_CATEGORY.value,
        group: COMMON_STOCK_CATEGORY.group,
      },
      {
        name: PREFERRED_STOCK_CATEGORY.name,
        value: PREFERRED_STOCK_CATEGORY.value,
        group: PREFERRED_STOCK_CATEGORY.group,
      },
      {
        name: TREASURY_STOCK_CATEGORY.name,
        value: TREASURY_STOCK_CATEGORY.value,
        group: TREASURY_STOCK_CATEGORY.group,
      },
      {
        name: RETAINED_EARNINGS_CATEGORY.name,
        value: RETAINED_EARNINGS_CATEGORY.value,
        group: RETAINED_EARNINGS_CATEGORY.group,
      },
      {
        name: ADDITIONAL_PAID_IN_CAPITAL_CATEGORY.name,
        value: ADDITIONAL_PAID_IN_CAPITAL_CATEGORY.value,
        group: ADDITIONAL_PAID_IN_CAPITAL_CATEGORY.group,
      },
      {
        name: DIVIDENDS_CATEGORY.name,
        value: DIVIDENDS_CATEGORY.value,
        group: DIVIDENDS_CATEGORY.group,
      },
      {
        name: OWNER_DISTRIBUTION_CATEGORY.name,
        value: OWNER_DISTRIBUTION_CATEGORY.value,
        group: OWNER_DISTRIBUTION_CATEGORY.group,
      },
      {
        name: OWNER_AND_MEMBER_AND_PARTNER_CAPITAL_CATEGORY.name,
        value: OWNER_AND_MEMBER_AND_PARTNER_CAPITAL_CATEGORY.value,
        group: OWNER_AND_MEMBER_AND_PARTNER_CAPITAL_CATEGORY.group,
      },
      {
        name: NET_INCOME_CATEGORY.name,
        value: NET_INCOME_CATEGORY.value,
        group: NET_INCOME_CATEGORY.group,
      },
    ];
  }

  static getAllowedCurrentAssetsCategories() {
    const { currentAssets } = this.getAllowedBalanceSheetItemGroups();

    return Object.keys(FinancialAccount.getAllowedAssetsCategories()).filter(
      (categoryKey) => {
        const category =
          FinancialAccount.getAllowedAssetsCategories()[categoryKey];
        return category.group === currentAssets;
      }
    );
  }

  static getAllowedLongTermAssetsCategories() {
    const { longTermAssets } = this.getAllowedBalanceSheetItemGroups();

    return Object.keys(FinancialAccount.getAllowedAssetsCategories()).filter(
      (categoryKey) => {
        const category =
          FinancialAccount.getAllowedAssetsCategories()[categoryKey];
        return category.group === longTermAssets;
      }
    );
  }

  static getAllowedLongTermLiabilitiesCategories() {
    const { longTermLiabilities } = this.getAllowedBalanceSheetItemGroups();

    return Object.keys(
      FinancialAccount.getAllowedLiabilitiesCategories()
    ).filter((categoryKey) => {
      const category =
        FinancialAccount.getAllowedLiabilitiesCategories()[categoryKey];
      return category.group === longTermLiabilities;
    });
  }

  static getAllowedCurrentLiabilitiesCategories() {
    const { currentLiabilities } = this.getAllowedBalanceSheetItemGroups();

    return Object.keys(
      FinancialAccount.getAllowedLiabilitiesCategories()
    ).filter((categoryKey) => {
      const category =
        FinancialAccount.getAllowedLiabilitiesCategories()[categoryKey];
      return category.group === currentLiabilities;
    });
  }
}
